<template>
  <div class="content-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div :class="[ isCollapsed ? 'collapsed-col' : 'custom-col-width-10', 'content']" >
        <div class="body">
          <navbar />
          <!-- Mobile / tablet top app bar (hidden on large screens) -->
          <div class="m-topbar">
            <img class="m-topbar__logo" :src="brandLogo" alt="Chabad Digital" />
            <div class="m-topbar__actions">
              <button class="m-icon-btn" title="Notifications">
                <i class="pi pi-bell"></i>
              </button>
              <button class="m-icon-btn" title="Refresh" v-on:click="getScreens({ logRefresh: true })">
                <i class="pi pi-refresh"></i>
              </button>
              <span class="m-avatar">{{ accountInitials }}</span>
            </div>
          </div>
          <div class="dashboard-page">
          <div class="dash-header">
            <div class="dash-header__titles">
              <h4 class="page-title">Dashboard</h4>
              <h6 class="page-subtitle">Manage your display screens here.</h6>
            </div>
            <div class="dash-header__actions">
              <date-time-range-picker
                class="dash-preview-picker"
                :date="activeFilterDate"
                :today="screenToday"
                :start="rangeStart"
                :end="rangeEnd"
                :now-minutes="screenNowMinutes"
                @apply="onPreviewApply"
              />
            </div>
          </div>
          <div class="row dash-body">
            <!-- Left column: stat cards + main display -->
            <div class="col-9 dash-main">
              <div class="display-panel">
                <div v-if="selectedScreen" class="display-toolbar">
                  <div class="display-toolbar__left">
                    <div class="dropdown">
                      <button class="screen-select dropdown-toggle" type="button" data-toggle="dropdown" id="dashScreenSelect">
                        <span class="screen-select__dot" :class="{ 'is-off': !selectedScreen.isActive }"></span>
                        {{ selectedScreen.name }}
                        <i class="pi pi-chevron-down"></i>
                      </button>
                      <div class="dropdown-menu" aria-labelledby="dashScreenSelect">
                        <a v-for="(screen, i) in screens" :key="i" class="dropdown-item" v-on:click="selectedScreen = screen">{{ screen.name }}</a>
                      </div>
                    </div>
                  </div>
                  <div class="display-toolbar__right">
                    <button class="toolbar-icon-btn" title="Refresh" v-on:click="refreshScreen(selectedScreen)">
                      <i class="pi pi-refresh"></i>
                    </button>
                    <button class="toolbar-icon-btn" title="Open Live Display" v-on:click="openDisplay(selectedScreen)">
                      <i class="pi pi-window-maximize"></i>
                    </button>
                  </div>
                </div>
                <div class="display-stage">
                  <div v-if="shouldShowDisplayLoader" class="loading-container">
                    <div class="loading-content">
                      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <p>Loading Screen... </p>
                    </div>
                  </div>
                  <div v-else-if="selectedScreen" class="dashboard-display-shell">
                    <DisplayRenderer
                      :key="selectedScreenPreviewKey"
                      :screen-override="selectedScreen"
                      :preview-date="renderedPreview.date"
                      :preview-start-time="renderedPreview.startTime"
                      :preview-end-time="renderedPreview.endTime"
                      embedded
                      :show-controls="false"
                    />
                  </div>
                  <div v-else class="display-empty">
                    <i class="pi pi-desktop"></i>
                    <p>No screen selected</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right column: screens rail -->
            <div class="col-3 dash-rail">
              <div class="row dash-stats">
                <div class="col-12">
                  <div class="stat-card dash-stat--compact">
                    <span class="stat-card__label">Total Active Screens</span>
                    <span class="stat-card__value">{{ pad2(activeScreens.length) }}</span>
                    <span v-if="newScreensToday" class="stat-card__delta stat-card__delta--up">
                      +{{ newScreensToday }} new <span class="stat-card__muted">Screens added</span>
                    </span>
                    <span v-else class="stat-card__delta stat-card__muted">{{ totalScreens }} total screens</span>
                  </div>
                </div>
                <div class="col-12">
                  <div class="stat-card dash-stat--compact">
                    <span class="stat-card__label">Announcements Today</span>
                    <span class="stat-card__value">{{ pad2(announcementsCount) }}</span>
                    <span class="stat-card__delta stat-card__delta--info">{{ scheduledAnnouncementsCount }} scheduled</span>
                  </div>
                </div>
                <div class="col-12">
                  <div class="stat-card dash-stat--compact">
                    <span class="stat-card__label">Scheduled Items</span>
                    <span class="stat-card__value">{{ pad2(scheduledItemsCount) }}</span>
                    <span class="stat-card__delta stat-card__muted">across {{ totalScreens }} screens</span>
                  </div>
                </div>
              </div>
              <div class="screens-rail">
                <div class="screens-rail__head">
                  <div class="screens-rail__title">
                    Screens <span class="screens-rail__count">{{ totalScreens }}</span>
                  </div>
                </div>
                <!-- <div class="segmented screens-tabs">
                  <button class="segmented__option" :class="{ 'is-active': activeTab === 'all' }" v-on:click="activeTab = 'all'">
                    All <span class="screens-tabs__count">{{ totalScreens }}</span>
                  </button>
                  <button class="segmented__option" :class="{ 'is-active': activeTab === 'active' }" v-on:click="activeTab = 'active'">
                    Active <span class="screens-tabs__count">{{ activeScreens.length }}</span>
                  </button>
                  <button class="segmented__option" :class="{ 'is-active': activeTab === 'offline' }" v-on:click="activeTab = 'offline'">
                    Offline <span class="screens-tabs__count">{{ offlineScreens.length }}</span>
                  </button>
                </div> -->
                <div class="screens-list">
                  <template v-for="(screen, index) in filteredScreens" :key="index">
                    <div
                      class="screen-row"
                      :class="{ 'is-active': selectedScreen && selectedScreen.id === screen.id }"
                      v-on:click="selectedScreen = screen"
                    >
                      <div class="screen-row__thumb" :style="{ backgroundImage: `url(${getScreenImage(screen.image)})` }"></div>
                      <div class="screen-row__meta">
                        <div class="screen-row__name">{{ screen.name }}</div>
                        <div class="screen-row__status">
                          <span class="status-pill" :class="screen.isActive ? 'status-pill--active' : 'status-pill--offline'">
                            {{ screen.isActive ? 'Active' : 'Offline' }}
                          </span>
                          <span class="screen-row__sub">{{ screen.isActive ? 'Live now' : 'Disconnected' }}</span>
                        </div>
                      </div>
                      <div class="screen-row__actions" v-on:click.stop>
                        <button class="screen-row__icon-btn" title="Refresh" v-on:click="refreshScreen(screen)">
                          <i class="pi pi-refresh"></i>
                        </button>
                        <button class="screen-row__icon-btn" title="Open Display" v-on:click="openDisplay(screen)">
                          <i class="pi pi-link"></i>
                        </button>
                      </div>
                    </div>
                  </template>
                  <div v-if="!screensIsLoading && filteredScreens.length === 0" class="screens-empty">
                    No screens to show.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile / tablet sync status strip (hidden on large screens) -->
          <div class="m-syncbar">
            <span class="m-syncbar__status">
              <span class="m-syncbar__dot"></span>
              {{ activeScreens.length }} of {{ totalScreens }} screens live
            </span>
            <span class="m-syncbar__ver">SK 2.1.3</span>
          </div>
          </div>

          <!-- Mobile / tablet bottom navigation (hidden on large screens) -->
          <bottom-nav />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios'
import sidenav from '@/components/Sidenav.vue';
import navbar from '@/components/Navbar.vue';
import DisplayRenderer from '@/views/Display.vue';
import bottomNav from '@/components/Bottom_Nav.vue';
import DateTimeRangePicker from '@/components/Date_Time_Range_Picker.vue';
import { GET_ORGANIZATION, GET_SCREENS, REFRESH_SCREEN } from '@/store/action_types';
import { DAYS_OF_THE_WEEK, PARSHA_NAMES, POSTER_IMAGES_DIR, SCREEN_IMAGES_DIR, STORAGE_URL, TOAST_GLOBAL_CONFIG, ZMANIM_YIDDISH_MAPPING } from '@/utils/const';
import { DISPLAY_ROUTE } from '@/router/routes';
import { hasScheduleStarted } from '@/utils/display_schedule';
import {
  DAY_MINUTES,
  getDateValueInTimeZone,
  getPreviewOpeningInstant,
  getZonedDateTimeParts,
  parseDateValue,
  timeValueFromMinutes,
  toLocalNoon,
} from '@/utils/display_preview';
import brandLogo from '@/assets/images/logo.png';

import '@vueup/vue-quill/dist/vue-quill.core.css'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

export default {
  title: 'Dashboard',
  components: {
    sidenav,
    navbar,
    DisplayRenderer,
    bottomNav,
    DateTimeRangePicker
  },
  data() {
    return {
      ZMANIM_YIDDISH_MAPPING,
      brandLogo,
      isSidenavCollapsed: false,
      activeTab: 'all',
      selectedScreen: null,
      imageWidth: 1,
      imageHeight: 1,
      imageNaturalWidth: 1920, // Original design width for scale calculation
      imageReady: false,
      selectedArtboard: null,
      // This page's own copy of the screens, fetched for the date on show. The
      // store's list is shared — the Navbar loads it too, with no date at all —
      // and a late answer there must never replace the preview's content.
      dashboardScreens: null,
      // The page fetches its screens as it mounts.
      isFetchingScreens: true,
      // Only the newest fetch may land: dates can be picked faster than they load.
      screensRequestId: 0,
      // The date picked in the header's picker, or null for today — which moves
      // on at the screen's own midnight.
      previewDate: null,
      // The time range of that date the preview covers, in minutes past midnight:
      // midnight to the next midnight unless the picker narrows it. The
      // backend hands over everything on air at any moment of it, and the display
      // runs exactly as live — at the current time of day while that falls
      // inside the range, else from the range's start.
      rangeStart: 0,
      rangeEnd: DAY_MINUTES,
      // What the screens on show were fetched for (see previewRequest). It
      // changes together with them, so the display never runs one preview's
      // clock over another preview's content.
      renderedPreview: { previewDate: null, date: null, start: 0, end: DAY_MINUTES, startTime: '', endTime: '' },
      isOverflowing: {}, // { [artboardId]: true/false }
      displayItemIndexes: {}, // { [artboardId]: number }
      currentTime: new Date(),
      isBackgroundLoading: true,
      displayItemIndex: -1, // Start before the first item
      zmanims: null,
      artboardTimers: {},
      parshaName: '',
      today: null,
    }
  },
  watch: {
    selectedScreen: {
      immediate: true,
      handler(newVal, oldVal) {
        if (oldVal && oldVal !== newVal) {
          Object.values(this.artboardTimers).forEach((timerId) => clearInterval(timerId));
          this.artboardTimers = {};
        }
        if (newVal) {
          this.imageReady = false;
        }
      }
    },
  },
  computed: {
    screens() {
      return this.dashboardScreens;
    },
    screensIsLoading() {
      return this.isFetchingScreens;
    },
    shouldShowDisplayLoader() {
      return this.screensIsLoading && !this.selectedScreen;
    },
    screensMetaData() {
      return this.$store.getters.screens.metaData;
    },
    isCollapsed() {
      return this.$store.getters.isCollapsed;
    },
    organization() {
      return this.$store.getters.account.organization;
    },
    account() {
      return this.$store.getters.account.data;
    },
    // Initials for the mobile top-bar avatar (mirrors the desktop navbar).
    accountInitials() {
      const a = this.account || {};
      const f = (a.fname || '').trim();
      const l = (a.lname || '').trim();
      if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase();
      const e = (a.email || '').trim();
      return e ? e.slice(0, 2).toUpperCase() : '··';
    },
    // Scale factor for proportional content scaling
    scaleFactor() {
      return this.imageWidth / this.imageNaturalWidth;
    },
    // ---- Dashboard summary (derived from the loaded screens) ----
    totalScreens() {
      return (this.screens || []).length;
    },
    activeScreens() {
      return (this.screens || []).filter((s) => s.isActive);
    },
    offlineScreens() {
      return (this.screens || []).filter((s) => !s.isActive);
    },
    newScreensToday() {
      const now = new Date();
      return (this.screens || []).filter((s) => {
        if (!s.createdAt) return false;
        const d = new Date(s.createdAt);
        return d.getFullYear() === now.getFullYear()
          && d.getMonth() === now.getMonth()
          && d.getDate() === now.getDate();
      }).length;
    },
    allArtboards() {
      return (this.screens || []).flatMap((s) => s.artboards || []);
    },
    announcementsCount() {
      return this.allArtboards.reduce((n, a) => n + (a.announcements?.length || 0), 0);
    },
    // Today's announcements still waiting for their start time, read the way the
    // display reads them: on each screen's own clock, at the moment the preview
    // opens on (the current time of day, or the start of a range that misses it).
    scheduledAnnouncementsCount() {
      const { date, startTime, endTime } = this.renderedPreview;
      return (this.screens || []).reduce((count, screen) => {
        const timeZone = this.getConfiguredTimeZone(screen);
        const clockZone = timeZone || this.deviceTimeZone;
        const now = (date && getPreviewOpeningInstant(date, startTime, endTime, clockZone, this.currentTime))
          || this.currentTime;
        return count + (screen.artboards || [])
          .flatMap((a) => a.announcements || [])
          .filter((item) => item?.displayStartDate && !hasScheduleStarted(item.displayStartDate, timeZone, now))
          .length;
      }, 0);
    },
    scheduledItemsCount() {
      return this.allArtboards.reduce((n, a) => n + (
        (a.donations?.length || 0)
        + (a.announcements?.length || 0)
        + (a.minyanims?.length || 0)
        + (a.zmanims?.length || 0)
        + (a.dateTimeBanners?.length || 0)
        + (a.posters?.length || 0)
        + (a.minyanimCountdowns?.length || 0)
      ), 0);
    },
    filteredScreens() {
      if (this.activeTab === 'active') return this.activeScreens;
      if (this.activeTab === 'offline') return this.offlineScreens;
      return this.screens || [];
    },
    // A new preview re-keys the display so it starts over on that preview's clock.
    selectedScreenPreviewKey() {
      const { date, startTime, endTime } = this.renderedPreview;
      return this.selectedScreen
        ? `${this.selectedScreen.id}-${date || 'live'}-${startTime}-${endTime}`
        : 'no-screen';
    },
    deviceTimeZone() {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      } catch (error) {
        return 'UTC';
      }
    },
    // The timezone the selected screen's display reads its day in.
    selectedScreenTimeZone() {
      return this.getConfiguredTimeZone(this.selectedScreen) || this.deviceTimeZone;
    },
    // Today on the selected screen's own clock: the day its live display is on.
    screenToday() {
      return getDateValueInTimeZone(this.currentTime, this.selectedScreenTimeZone)
        || this.formatDateInputValue(this.currentTime);
    },
    // The date the preview shows, and so the one the picker shows.
    activeFilterDate() {
      return this.previewDate || this.screenToday;
    },
    // The current time of day on the selected screen's own clock, in minutes —
    // for the picker's "Now" and the marker on its timeline.
    screenNowMinutes() {
      const parts = getZonedDateTimeParts(this.currentTime, this.selectedScreenTimeZone);
      if (parts) return parts.hour * 60 + parts.minute;
      return this.currentTime.getHours() * 60 + this.currentTime.getMinutes();
    },
    // What a fetch asks for: the picked date (today, by default) across the
    // picked time range of it (the whole day, by default).
    previewRequest() {
      return {
        previewDate: this.previewDate,
        date: this.previewDate || this.screenToday,
        start: this.rangeStart,
        end: this.rangeEnd,
        startTime: timeValueFromMinutes(this.rangeStart),
        endTime: timeValueFromMinutes(this.rangeEnd),
      };
    },
  },
  methods: {
    pad2(n) {
      return String(n ?? 0).padStart(2, '0');
    },
    formatDateInputValue(date) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    formatDateTimeForFilter(date) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      const ss = String(date.getSeconds()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    },
    // The timezone a screen's display reads its day in (Display.vue
    // getScreenLocationConfig): its own, else its organization's; '' when
    // neither has one.
    getConfiguredTimeZone(screen) {
      const zone = [screen?.timezone, screen?.organization?.timezone]
        .find((value) => typeof value === 'string' && value.trim());
      return zone ? zone.trim() : '';
    },
    openDisplay(screen) {
      const routeData = this.$router.resolve({ name: DISPLAY_ROUTE, params: { id: screen.id }, query: { postCode: this.account.organization.postCode } });
      window.open(routeData.href, '_blank');
    },
    refreshScreen(screen) {
      this.$store.dispatch(REFRESH_SCREEN, { id: screen.id }).then((res) => {
        if(res) {
          console.log('REFRESHED');
          this.$toast.success('Screen Refreshed', TOAST_GLOBAL_CONFIG)
        }
      })
    },
    onImageLoad() {
      this.imageEl = this.$refs.imageEl;
      this.imageWidth = this.imageEl.clientWidth;
      this.imageHeight = this.imageEl.clientHeight;
      this.imageNaturalWidth = this.imageEl.naturalWidth || 1920;
      this.imageReady = true;
    },
    handleResize() {
      if (this.$refs.imageEl) {
        this.imageWidth = this.$refs.imageEl.clientWidth;
        this.imageHeight = this.$refs.imageEl.clientHeight;
      }
    },
    // Scale artboard content proportionally using CSS transform
    getScaledContentStyle(artboard) {
      const displayedWidth = artboard.widthPercent * this.imageWidth;
      const displayedHeight = artboard.heightPercent * this.imageHeight;
      // Size boost multiplier - increase content size by 100%
      const sizeBoost = 2.0;
      const adjustedScale = this.scaleFactor * sizeBoost;
      // Original size before scaling
      const originalWidth = displayedWidth / adjustedScale;
      const originalHeight = displayedHeight / adjustedScale;
      
      return {
        width: originalWidth + 'px',
        height: originalHeight + 'px',
        transform: `scale(${adjustedScale})`,
        transformOrigin: 'top left',
      };
    },
    // Donor-entered fields (title / name / inHonorOf …) may span multiple lines.
    // Convert their newlines to <br> so the selected-screen preview matches the
    // live Display (raw "\n" would otherwise collapse to a single space).
    multilineToHtml(value) {
      if (value == null) return '';
      return String(value).replace(/\r\n|\r|\n/g, '<br>');
    },
    renderContent(template, donation) {
        let replacements = []

        Object.keys(donation).forEach(key => {
            const regex = new RegExp(`{${key}}`, 'g');
            if (template.includes(`{${key}}`)) {
                const value = key == 'title' ? `${donation['title']}`
                              : key == 'name' ? `${donation['name']}`
                              : key == 'donationDate' ? `${this.$filters.getDateOnly(donation.createdAt)}`
                              : key == 'displayStartDate' ? `${this.$filters.getDateOnly(donation.displayStartDate)}`
                              : key == 'displayEndDate' ? `${this.$filters.getDateOnly(donation.displayEndDate)}`
                              : donation[key];
                replacements.push({
                    replace: regex,
                    replaceBy: this.multilineToHtml(value)
                })
            }

        });

        replacements.forEach((e) => {
            template = template.replace(e.replace, () => e.replaceBy)
        })

        if(template.includes('{month}')) {
          template = template.replace('{month}', () => this.$filters.convertToHebrewMonthOnly(donation.displayStartDate))
        }

        return template;
    },
    renderZmanimContent(template, zmanims) {
      if (!zmanims) return template;

      // This regex matches {key} ... {time}
      return template.replace(/{(\w+)}([^{}]*?){time}/g, (match, key, between) => {
        const value = zmanims[key] || '';
        // Yiddish mapping or fallback to key
        const keyLabel = ZMANIM_YIDDISH_MAPPING[key] || key;
        let timeStr = '';
        if (value && typeof value === 'string') {
          const date = new Date(value);
          if (!isNaN(date)) {
            timeStr = date?.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }).replace(/ AM| PM/i, '');
          }
        }
        // Replace {key} with label, keep the text in between, replace {time} with timeStr
        return `${keyLabel}${between}${timeStr}`;
      });
    },
    renderDateTimeBanner(content) {
      content = String(content || '');
      // Get current time in 12-hour format (HH:MM)
      const now = this.currentTime;
      let timeStr = now?.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(/ AM| PM/i, '');

      // Remove leading zero from hour
      const [hour, minute] = timeStr.split(':');
      timeStr = `${Number(hour)}:${minute}`;

      // Get Hebrew date parts
      const hebrewDate = this.$filters.convertToHebrewDateObject(now);

      content = content.replace(/{time}/g, timeStr);
      content = content.replace(/{dayOfWeek}/g, hebrewDate.dayOfWeek || '');
      content = content.replace(/{dayOfMonth}/g, hebrewDate.dayOfMonth || '');
      content = content.replace(/{month}/g, hebrewDate.month || '');
      content = content.replace(/{year}/g, hebrewDate.year || '');
      content = content.replace(/{parsha}/g, this.getSafeParshaName());

      content = content.replace(/\bundefined\b/g, '').replace(/\bnull\b/g, '');
      content = content.replace(/[ \t]{2,}/g, ' ');

      return content
    },
    getDropdownStyle(artboard) {
      return {
        position: 'absolute',
        top: artboard.topPercent * this.imageHeight * 1.1 + 'px',
        left: artboard.leftPercent * this.imageWidth + 'px',
        width: artboard.widthPercent * this.imageWidth + 'px',
        height: artboard.heightPercent * this.imageHeight + 'px',
        border: '1px dashed #2ecc71',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        overflow: 'auto'
      };
    },
    getPosterImage(image) {
      return `${STORAGE_URL}/${POSTER_IMAGES_DIR}/${image}`
    },
    getArtboardStyle(artboard) {
      return {
        position: 'absolute',
        top: artboard.topPercent * this.imageHeight + 'px',
        left: artboard.leftPercent * this.imageWidth + 'px',
        width: artboard.widthPercent * this.imageWidth + 'px',
        height: artboard.heightPercent * this.imageHeight + 'px',
        border: '1px dashed #2ecc71',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        overflow: 'auto'
      };
    },
    startArtboardCarousel(artboard) {
      const elements = this.getDisplayElements(artboard);
      if (!elements.length) return;

      // Cleanup previous timer if exists
      if (this.artboardTimers[artboard.id]) {
        clearInterval(this.artboardTimers[artboard.id]);
      }

      // Set to 0 so it starts at the beginning
      this.displayItemIndexes[artboard.id] = 0;

      // Only start interval if there is more than one element
      if (elements.length > 1) {
        this.artboardTimers[artboard.id] = setInterval(() => {
          const currentIndex = this.displayItemIndexes[artboard.id] || 0;
          const nextIndex = (currentIndex + 1) % elements.length;
          this.displayItemIndexes[artboard.id] = nextIndex;
        }, 60000);
      }
    },
    emitGetScreen() {
      this.$emit('refreshScreen');
    },
    getScreenImage(image) {
      return `${STORAGE_URL}/${SCREEN_IMAGES_DIR}/${image}`;
    },
    formatHebcalDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async getParsha() {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay() + 1); // Monday
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6); // Sunday

      try {
        const parshaRes = await axios.get(`https://www.hebcal.com/leyning`, {
          params: {
            cfg: 'json',
            start: this.formatHebcalDate(startDate),
            end: this.formatHebcalDate(endDate)
          },
        });

        const todayStr = this.formatHebcalDate(today);
        const parshaItems = (parshaRes && parshaRes.data && Array.isArray(parshaRes.data.items))
          ? parshaRes.data.items
          : [];
        const parshaItem = this.getBestParshaItem(parshaItems, todayStr);
        this.parshaName = this.getMappedParshaName(parshaItem)
      } catch (error) {
        console.error('Failed to fetch parsha', error);
        this.parshaName = '';
      }
    },
    getParshaEnglishName(parshaItem) {
      const name = parshaItem?.name;
      if (!name) return '';
      return (typeof name === 'string' ? name : name.en || '').trim();
    },
    getParshaHebrewName(parshaItem) {
      const name = parshaItem?.name;
      if (!name || typeof name === 'string') return '';
      return name.he || '';
    },
    getParshaLookupCandidates(name) {
      const normalized = String(name || '')
        .trim()
        .replace(/[\u2018\u2019\u02bc]/g, "'")
        .replace(/[\u2010-\u2015]/g, '-')
        .replace(/\s+/g, ' ');
      const withoutPrefix = normalized.replace(/^(Parashat|Parsha|Parshat)\s+/i, '');
      const candidates = new Set([normalized, withoutPrefix]);

      [normalized, withoutPrefix].forEach((candidate) => {
        candidates.add(candidate.replace(/\bNaso\b/g, 'Nasso'));
        candidates.add(candidate.replace(/\bNasso\b/g, 'Naso'));
      });

      return Array.from(candidates).filter(Boolean);
    },
    hasMappedParshaName(parshaItem) {
      const result = this.getParshaEnglishName(parshaItem);
      const parshaNames = PARSHA_NAMES;
      return this.getParshaLookupCandidates(result).some((candidate) => parshaNames[candidate]);
    },
    getBestParshaItem(parshaItems, todayStr) {
      if (!Array.isArray(parshaItems) || !parshaItems.length) return null;

      const todayItem = parshaItems.find((item) => item.date === todayStr);
      if (todayItem?.parshaNum && this.hasMappedParshaName(todayItem)) return todayItem;

      return parshaItems.find((item) => item?.parshaNum && this.hasMappedParshaName(item))
        || parshaItems.find((item) => this.hasMappedParshaName(item))
        || todayItem
        || parshaItems[0];
    },
    stripHebrewVowels(text) {
      return String(text || '')
        // The API joins multi-word parsha names with a maqaf (U+05BE)
        // rather than a space. Convert it to a space first so the word gap
        // survives instead of being dropped with the vowel points below.
        .replace(/־/g, ' ')
        // Remove cantillation marks and niqqud (Hebrew points U+0591..U+05C7).
        .replace(/[֑-ׇ]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    withParshaPrefix(name, parshaItem) {
      const value = String(name || '').trim();
      if (!value) return '';
      if (!parshaItem?.parshaNum) return value;
      return value.includes('פרשת') ? value : `פרשת ${value}`;
    },
    getMappedParshaName(parshaItem) {
      if (!parshaItem) return '';
      const result = this.getParshaEnglishName(parshaItem);
      const parshaNames = PARSHA_NAMES;
      const mappedName = this.getParshaLookupCandidates(result)
        .map((candidate) => parshaNames[candidate])
        .find(Boolean);

      if (mappedName) return this.withParshaPrefix(mappedName, parshaItem);

      const hebrewName = this.stripHebrewVowels(this.getParshaHebrewName(parshaItem));
      if (hebrewName) return this.withParshaPrefix(hebrewName, parshaItem);

      return result || '';
    },
    getSafeParshaName() {
      const raw = this.parshaName;
      if (raw == null) return '';
      const value = String(raw).trim();
      if (!value || ['undefined', 'null'].includes(value.toLowerCase())) return '';
      return value;
    },
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    hasFeature(artboard, ...featureNames) {
      const features = Array.isArray(artboard?.features) ? artboard.features : [];
      return featureNames.some((featureName) => features.includes(featureName));
    },
    getDisplayElements(artboard) {
      const features = Array.isArray(artboard?.features) ? artboard.features : [];
      const result = [
        ...(artboard.donations || []),
        ...(artboard.announcements || []),
        ...(artboard.minyanims || []),
        ...(artboard.zmanims || []),
        ...(artboard.dateTimeBanners || []),
        ...(artboard.posters || []),
        ...(artboard.minyanimCountdowns || []),
      ];
      if (features.includes('MinyanRoll')) {
        result.push(
          ...(artboard.minyanRolls || [])
            .filter((roll) => roll && roll.isActive !== false)
            .map((roll) => ({ ...roll, _isMinyanRoll: true }))
        );
      }
      if (this.hasFeature(artboard, 'UpcomingMinyanim', 'UpcomingMinyan')) {
        result.push(
          ...(artboard.upcomingMinyanims || [])
            .filter((upcoming) =>
              upcoming
              && upcoming.enabled === true
              && upcoming.isActive !== false
              && !upcoming.isDeleted
            )
            .map((upcoming) => ({ ...upcoming, _isUpcomingMinyan: true }))
        );
      }
      return result;
    },
    renderMinyanimCountdownContent(minyanimCountdown) {
      const now = this.currentTime ? this.currentTime.getTime() : Date.now();

      let minyanimRooms = this.selectedScreen?.artboards
        .flatMap(artboard => artboard.minyanims || [])
        .flatMap(type =>
          (type.minyanimRooms || []).map(room => ({
            ...room,
            minyanimTypeName: type.name,
          }))
        ) || [];

      const nowDate = this.currentTime || new Date();
      const nowMinutes = nowDate.getHours() * 60 + nowDate.getMinutes();

      let nextMinyanimRoom = minyanimRooms.reduce((closest, item) => {
        const itemDate = new Date(item.time);
        const itemMinutes = itemDate.getHours() * 60 + itemDate.getMinutes();

        if (itemMinutes < nowMinutes) return closest;
        if (!closest) return item;

        const closestDate = new Date(closest.time);
        const closestMinutes = closestDate.getHours() * 60 + closestDate.getMinutes();

        return itemMinutes < closestMinutes ? item : closest;
      }, null);

      if (!nextMinyanimRoom) {
        return '';
      }

      let content = minyanimCountdown?.content?.replace('{room}', nextMinyanimRoom.room.name);
      content = content?.replace('{type}', nextMinyanimRoom.minyanimTypeName);
      content = content?.replace('{time}', this.$filters.getTimeOnly12Hrs(nextMinyanimRoom.time));
      
      const countdownStr = this.formatMinyanimCountdownTo(nextMinyanimRoom.time);
      content = content?.replace('{countdown}', countdownStr);

      return content;
    },
    formatMinyanimCountdownTo(time) {
      if (!time || !this.currentTime) return '';

      const now = this.currentTime;
      const src = new Date(time);

      const target = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        src.getHours(),
        src.getMinutes(),
        src.getSeconds(),
        0
      );

      const nowMs = now.getTime();
      const targetMs = target.getTime();
      let diff = targetMs - nowMs;

      if (diff <= 0) {
        return '00:00:00';
      }

      const totalSeconds = Math.floor(diff / 1000);
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');

      return `${hours}:${minutes}:${seconds}`;
    },
    async fetchZmanimByZip() {
      console.log('FETCHING ZMANIN')
      try {
        console.log('this.$route.params.postCode', this.$route.query.postCode)
        const geoRes = await axios.get(`https://api.zippopotam.us/us/${this.account.organization.postCode}`);
        const place = geoRes.data.places[0];
        const lat = place.latitude;
        const lon = place.longitude;

        const tzid = 'America/New_York'; // For production, detect dynamically
        const zmanimRes = await axios.get(`https://www.hebcal.com/zmanim`, {
          params: {
            cfg: 'json',
            latitude: lat,
            longitude: lon,
            tzid: tzid,
          },
        });

        this.zmanims = zmanimRes.data?.times;

        console.log('this.zmanims => ', this.zmanims.sunrise)

        const hanachatTefillinISO = this.addMinutesWithOffset(this.zmanims.sunrise, -50);
        const alosHashacharISO = this.addMinutesWithOffset(this.zmanims.sunrise, -72);
        const plagMinchaMGA90ISO = this.addMinutesWithOffset(this.zmanims.sunrise, 361);
        const afterSunset60ISO = this.addMinutesWithOffset(this.zmanims.sunset, 60);

        let updatedZmanims = {
          ...this.zmanims,
          'hanachatTefillin': hanachatTefillinISO,
          'alosHashachar': alosHashacharISO,
          'plagMinchaMGA90': plagMinchaMGA90ISO,
          'afterSunset60': afterSunset60ISO,
        }

        this.zmanims = updatedZmanims

        console.log('this.updatedZmanims => ', updatedZmanims)
        console.log('this.zmanims (AFTER UPDATE) => ', this.zmanims)

      } catch (e) {
        console.log('error', e)
        // this.$toast.error('Unable to fetch Zmanims.', TOAST_GLOBAL_CONFIG)
      }
    },
    addMinutesWithOffset(originalStr, minutesToAdd) {
      // Parse original offset like "-04:00" -> -240 minutes
      const m = originalStr.match(/([+-]\d{2}):(\d{2})$/);
      const offsetMinutes = m ? (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) : 0;

      // Work in UTC
      const origUtcMs = new Date(originalStr).getTime();
      const shiftedUtcMs = origUtcMs + minutesToAdd * 60000;

      // Convert to "wall time" in the original offset by adding the offset to UTC,
      // then read components with getUTC* (so we don't use your computer's timezone).
      const wallMs = shiftedUtcMs + offsetMinutes * 60000;
      const wall = new Date(wallMs);

      const pad = n => String(n).padStart(2, '0');
      const yyyy = wall.getUTCFullYear();
      const mm = pad(wall.getUTCMonth() + 1);
      const dd = pad(wall.getUTCDate());
      const hh = pad(wall.getUTCHours());
      const mi = pad(wall.getUTCMinutes());
      const ss = pad(wall.getUTCSeconds());
      const offset = originalStr.slice(-6); // keep original "-04:00"

      return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}${offset}`;
    },
    getZmanimTime(key) {
      if (!this.zmanims) return '- 00:00 -';

      const value = this.zmanims[key] || '';
      // Yiddish mapping or fallback to key
      const keyLabel = ZMANIM_YIDDISH_MAPPING[key] || key;

      const dateMatch = value.match(/T(\d{2}):(\d{2}):(\d{2})/);
      let hour = dateMatch ? parseInt(dateMatch[1], 10) : 0;
      let minute = dateMatch ? dateMatch[2] : '00';
      let second = dateMatch ? dateMatch[3] : '00';
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12; // 12-hour format

      console.log('this.keyy', key)
      console.log('ZMANIM_YIDDISH_MAPPING[key]', key)
      console.log('this.zmanimsthis.zmanims', this.zmanims)
      return `${hour}:${minute}`;
    },
    displayCarousel() {
      const x = document.getElementsByClassName("display-item");

      console.log('x.length', x.length);
      if (x.length === 0) return; // Ensure there are elements to animate

      // Hide the current item with fadeOut animation
      if (this.displayItemIndex >= 0 && this.displayItemIndex < x.length) {
        x[this.displayItemIndex].classList.remove("fadeIn");
        x[this.displayItemIndex].classList.add("fadeOut");
      }

      // Increment the index
      this.displayItemIndex = (this.displayItemIndex + 1) % x.length;

      // Show the next item with fadeIn animation after fadeOut completes
      setTimeout(() => {
        if (this.displayItemIndex >= 0 && this.displayItemIndex < x.length) {
          x[this.displayItemIndex].classList.remove("fadeOut");
          x[this.displayItemIndex].classList.add("fadeIn");
        }
      }, 1000); // Match this delay with the fadeOut animation duration (1s)

      // Schedule the next animation cycle
      setTimeout(() => {
        this.displayCarousel();
      }, 10000); // 10 seconds delay before switching to the next item
    },
    // The picker's Apply, or its reset: from now on the preview shows that day
    // across that range of it — the two arrive together, so one fetch covers both.
    onPreviewApply({ date, start, end }) {
      // Today stays null, so the preview follows the screen's own today.
      const previewDate = date && date !== this.screenToday ? date : null;
      if (previewDate === this.previewDate && start === this.rangeStart && end === this.rangeEnd) return;
      this.previewDate = previewDate;
      this.rangeStart = start;
      this.rangeEnd = end;
      this.getScreens();
    },
    getScreenImage(image) {
      return `${STORAGE_URL}/${SCREEN_IMAGES_DIR}/${image}`;
    },
    // Params for a screens fetch. The backend reads each screen in its own
    // timezone (its own, else its organization's, else the `timezone` hint
    // below), so screens in other timezones preview correctly from this browser:
    // `filterDate` is the day on show and `filterStartTime` / `filterEndTime` the
    // range of it, and everything on air at any moment of that range comes back.
    // The startDate / endDate / offset window stays for screens that have no
    // timezone configured anywhere.
    buildScreenFetchParams(request) {
      let timezone = '';
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch (error) {
        timezone = '';
      }

      const now = new Date();
      // The legacy window is the same range on this browser's clock, opening a
      // second after midnight so yesterday's content ending at midnight stays out.
      const day = parseDateValue(request.date);
      const onDay = (minutes, seconds) => {
        const moment = day ? toLocalNoon(day) : new Date(now.getTime());
        moment.setHours(Math.floor(minutes / 60), minutes % 60, seconds, 0);
        return moment;
      };
      const start = request.start > 0 ? onDay(request.start, 0) : onDay(0, 1);
      const end = request.end < DAY_MINUTES ? onDay(request.end, 0) : onDay(DAY_MINUTES - 1, 59);

      return {
        now: now.toISOString(),
        timezone,
        filterDate: day ? request.date : undefined,
        filterStartTime: request.startTime,
        filterEndTime: request.endTime,
        startDate: this.formatDateTimeForFilter(start),
        endDate: this.formatDateTimeForFilter(end),
        offset: now.getTimezoneOffset(),
        organizationId: this.account.organizationId,
      };
    },
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },
    checkOverflow(artboard) {
      this.$nextTick(() => {
        setTimeout(() => {
          let wrapperRef = this.$refs['artboardWrapper' + artboard.id];
          let scrollContentRef = this.$refs['artboardScrollContent' + artboard.id];

          // If multiple elements found, get the first one
          if (Array.isArray(wrapperRef)) wrapperRef = wrapperRef[0];
          if (Array.isArray(scrollContentRef)) scrollContentRef = scrollContentRef[0];

          if (wrapperRef && scrollContentRef) {
            // Force reflow
            wrapperRef.getBoundingClientRect();
            scrollContentRef.getBoundingClientRect();

            const wrapperHeight = wrapperRef.clientHeight;
            const contentHeight = scrollContentRef.scrollHeight;

            this.isOverflowing[artboard.id] = contentHeight > wrapperHeight;
          }
        }, 0);
      });
    },
    updateNearestFutureRoomIndexes() {
      if (!this.minyanimTypes) return;
      this.minyanimTypes.forEach((type, idx) => {
        this.nearestFutureRoomIndexes[idx] = this.getNearestFutureRoomIndex(type.minyanimRooms);
      });
    },
    updateNearestFutureZmanimIndexes() {
      if (!this.zmanimTypes) return;
      this.zmanims.forEach((type, idx) => {
        this.nearestFutureZmanimIndexes[idx] = this.getNearestFutureZmanimIndex(type.zmanimList);
      });
    },
    getNearestFutureRoomIndex(minyanimRooms) {
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();

      // Map each room to its time in minutes
      const futureRooms = minyanimRooms
        .map((room, idx) => ({
          idx,
          minutes: room.time ? (new Date(room.time).getHours() * 60 + new Date(room.time).getMinutes()) : null
        }))
        .filter(room => room.minutes !== null && room.minutes > nowMinutes);

      if (futureRooms.length === 0) return -1;

      // Find the room with the smallest minutes > nowMinutes
      futureRooms.sort((a, b) => a.minutes - b.minutes);
      return futureRooms[0].idx;
    },
    getZmanimDateTime(key) {
      if (!this.zmanims) return '- 00:00 -';

      const value = this.zmanims[key] || '';

      return value;
    },
    getNearestFutureZmanimIndex(zmanimList) {
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      const delay = Number(this.organization?.zmanimLabelDelay) || 0;

      // Map each room to its time in minutes
      const zmanimsWithMinutes = zmanimList
        .map((content, idx) => ({
          idx,
          minutes: this.getZmanimDateTime(content.name) ? (new Date(this.getZmanimDateTime(content.name)).getHours() * 60 + new Date(this.getZmanimDateTime(content.name)).getMinutes()) : null
        }))
        .filter(zmanim => zmanim.minutes !== null);

      // Find rooms within the delay window
      const windowStart = nowMinutes - delay;
      const windowEnd = nowMinutes + delay;

      const zmanimsInWindow = zmanimsWithMinutes
        .filter(zmanim => zmanim.minutes >= windowStart && zmanim.minutes <= windowEnd);

      console.log('zmanimsInWindowzmanimsInWindow',zmanimsInWindow.length)

      console.log('windowStartwindowStart', windowStart)
      console.log('windowEndwindowEnd', windowEnd)

      if (zmanimsInWindow.length === 0) return [];

      // Find the nearest zmanim in the window (closest to now)
      zmanimsInWindow.sort((a, b) => Math.abs(a.minutes - nowMinutes) - Math.abs(b.minutes - nowMinutes));
      return zmanimsInWindow.map((e) => e.idx);
    },
    getOrganization() {
      this.$store.dispatch(GET_ORGANIZATION, { id: this.account?.organizationId })
    },
    getScreens(options = {}) {
      const requestId = ++this.screensRequestId;
      const request = this.previewRequest;
      const selectedScreenId = this.selectedScreen?.id;
      const shouldLogRefresh = options.logRefresh ?? Boolean(selectedScreenId);
      this.isFetchingScreens = true;

      return this.$store.dispatch(GET_SCREENS, this.buildScreenFetchParams(request))
        .catch(() => false)
        .then((res) => {
          // Overtaken by a newer fetch (another preview picked meanwhile): that one lands.
          if (requestId !== this.screensRequestId) return;
          this.isFetchingScreens = false;
          if (!Array.isArray(res)) {
            // Nothing came back: the screens on show stay, and so does the
            // preview they were fetched for.
            this.previewDate = this.renderedPreview.previewDate;
            this.rangeStart = this.renderedPreview.start;
            this.rangeEnd = this.renderedPreview.end;
            return;
          }
          this.dashboardScreens = res;
          this.renderedPreview = request;
          this.selectedScreen = res.find((screen) => screen.id === selectedScreenId) || res[0] || null;
          if (shouldLogRefresh) {
            console.log('REFRESHED');
          }
        });
    },
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.today = DAYS_OF_THE_WEEK[new Date().getDay()];
    // The screens first: the preview of today is what this page opens on.
    this.getScreens()
    await this.fetchZmanimByZip()
    await this.getParsha()
    setInterval(() => { this.getScreens(), this.fetchZmanimByZip() }, 300000);
    setInterval(() => {
      this.updateNearestFutureRoomIndexes();
    }, 60000); // every 1 minute
    setInterval(() => {
      this.updateNearestFutureZmanimIndexes();
    }, 60000); // every 1 minute
    setInterval(() => {
      this.currentTime = new Date();
    }, 20000);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    Object.values(this.artboardTimers).forEach(intervalId => {
      clearInterval(intervalId);
    });
  },
}
</script>
<style scoped>
.body {
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.dashboard-display-shell {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

/* The embedded root fills the whole card; Display.vue then lets the background
   <img> be object-fit:contain inside it (native — always flush on the binding
   axis, width-flush when the height fits else height-flush) and offsets the
   artboard overlay to match the letterboxed image. */
.dashboard-display-shell :deep(.screen-display-root--embedded) {
  width: 100% !important;
  height: 100% !important;
}

.dashboard-display-shell :deep(.screen-display-root--embedded .image-container) {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;
  /* Full viewport height */
  width: 100%;
}

.loading-content {
  text-align: center;
}

.screen-card {
  padding: 0px;
  box-shadow: none;
}

.screen-card .pi:hover {
  cursor: pointer;
}

.card-body {
  padding: 10px;
}

.card-body .title {
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0px;
  color: #1D1F2C;
}

.card-body .donations {
  font-size: 12px;
  margin-bottom: 7px;
}

.card-body .badge-container {
  display: grid;
  place-items: center;
}

.card-body .pi {
  margin-left: 10px;
  color: #007bff;
  font-size: 13px;
}

.card-overlay .pi {
  font-size: 15px;
  margin-left: 20px;
  margin-right: 20px;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  border-radius: 10px;
  transition: opacity 0.3s ease-in-out;
}

.card:hover .card-overlay {
  opacity: 1;
}

.card .card-loading {
  opacity: 1;
}

.card-overlay i {
  font-size: 2rem;
  color: #fff;
}

.artboard-box {
  box-sizing: border-box;
}

.btn-custom-primary {
  padding: 0.5rem 1.6rem 0.5rem 1.6rem;
}

.btn-custom-primary .pi-ellipsis-h {
  font-size: 0.9rem;
}

.image-container {
  display: block;
  width: fit-content;
  margin: 0 auto;
  overflow: visible;
  position: relative;
}

.display-img {
  display: block;
  max-height: calc(100dvh - 160px);
  max-width: 100%;
  object-fit: contain;
  height: auto;
  width: auto;
}

html.app-zoomed .display-img {
  max-height: calc((100dvh - 160px) / 0.9);
}

.artboard-wrapper {
  position: absolute;
  /* border: 1px dashed #2ecc71;
  background-color: rgba(255, 255, 255, 0.8); */
  border: none!important;
  background-color: transparent!important;
  overflow: visible !important;
  z-index: 2;
}

.scrollable-section {
  max-height: 90vh; /* Set the maximum height for the scrollable area */
  overflow-y: auto; /* Enable vertical scrolling */
  margin-top: 13px;
}

.selected-screen-card {
  border: 1px solid var(--color-primary);
  box-shadow: 0 0 3px #8AA3DFFF; /* Add a glow effect */
  transition: box-shadow 0.3s ease-in-out;
}

.screen-card:hover {
  cursor: pointer;
  border: 1px solid var(--color-primary);
  box-shadow: 0 0 3px #8AA3DFFF; /* Add a glow effect */
  transition: box-shadow 0.3s ease-in-out;
}

.dropdown-container {
  position: absolute;
  top: -43px;
  left: 0;
  right: 0;
  z-index: 3;
}

.artboard-box-inner {
  overflow: auto;
  height: 100%;
}

.calendar-card {
  border-radius: 8px;
  padding: 0px;
  box-shadow: none;
  cursor: pointer;
  margin-top: 30px;
}

.calendar-card .card-body {
  padding-bottom: 0px;
}

.calendar-card .card-body h6,
.calendar-card .card-body .fa {
  font-size: 0.8rem!important;
  color: var(--color-primary);
}

.calendar-card .card-body .fa {
  font-size: 0.9rem!important;
  color: var(--color-primary);
  position: relative;
  bottom: 5px;
}

.minyanim-preview-list {
  font-weight: bold;
  font-size: 1rem;
  direction: rtl;
  margin-top: 12px;
}

.minyanim-preview-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 2px;
  font-size: 0.85rem;
  font-weight: 500;
}

.minyanim-room {
  margin-left: 8px;
  white-space: nowrap;
}

.minyanim-time {
  margin-right: 8px;
  white-space: nowrap;
}

.minyanim-dots {
  flex: 1 1 auto;
  border-bottom: 1px dotted #000;
  height: 0.7em;
  margin: 0 4px;
  opacity: 0.7;
  position: relative;
  top: -2px;
}

.artboard-box-inner {
  width: 100%;
  height: 100%;
  overflow: hidden; /* Hide scrollbars and allow credits animation */
  position: relative;
}

.artboard-box-inner {
  overflow: hidden !important;  /* Fully hides scrollbars */
  height: 100%;
}

.scaled-content {
  /* Content scales via transform: scale() applied inline */
  box-sizing: border-box;
}

.credits-track {
  flex-shrink: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.fadeIn {
  animation: fadeIn 1s forwards;
  display: block;
}

.fadeOut {
  animation: fadeOut 1s forwards;
  display: block;
}

.credits-scroll {
  display: inline-block;
  /* The animation duration should be proportional to content height for best effect */
  animation: credits-scroll 50s linear infinite;
}

.credits-scroll {
  overflow: hidden!important;        /* Hide overflow */
  scrollbar-width: none;   /* Firefox */
}

.credits-scroll::-webkit-scrollbar {
  display: none;           /* Chrome, Safari, Edge */
}

@keyframes credits-scroll {
  0% {
    transform: translateY(10%);
  }
  100% {
    transform: translateY(-100%);
  }
}

.credits-scroll-vertical {
  display: inline-block;
  animation: credits-scroll-vertical 50s linear infinite;
  overflow: hidden !important;
  scrollbar-width: none;
}
.credits-scroll-vertical::-webkit-scrollbar {
  display: none;
}

@keyframes credits-scroll-vertical {
  0% {
    transform: translateY(10%);
  }
  100% {
    transform: translateY(-100%);
  }
}

.credits-scroll-horizontal {
  white-space: nowrap;
  display: inline-block;
  animation: credits-scroll-horizontal 50s linear infinite;
  overflow: hidden !important;
  scrollbar-width: none;
  width: 100%;
  /* Optional: adjust height if needed */
}
.credits-scroll-horizontal::-webkit-scrollbar {
  display: none;
}

@keyframes credits-scroll-horizontal {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}

.custom-col-width-2 {
  width: var(--sidenav-expanded-width);
}

.custom-col-width-10 {
  width: calc(100% - var(--sidenav-expanded-width));
}

.collapsed-col {
  width: calc(100% - var(--sidenav-collapsed-width)) !important;
}

@media (max-width: 1000px) {
  .custom-col-width-2 {
    width: var(--sidenav-expanded-width-sm);
  }

  .custom-col-width-10 {
    width: calc(100% - var(--sidenav-expanded-width-sm));
  }
}

/* =========================================================================
   Dashboard redesign — header, compact stat cards, display panel + rail.
   Reuses the central theme tokens/patterns (stat-card, screens-rail,
   screen-row, status-pill, segmented, breadcrumb-row, btn-add).
   ========================================================================= */
.dashboard-page {
  padding: 8px 20px 20px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dash-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.dash-header__titles {
  min-width: 0;
}

.dash-header .breadcrumb-row {
  margin-left: 2px;
}

.dash-header .page-title {
  margin: 4px 0 2px 2px;
}

.dash-header .page-subtitle {
  margin: 0 0 0 2px;
}

.dash-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* The preview's date & time picker, nudged down to sit level with the titles. */
.dash-preview-picker {
  top: 10px;
}

.dash-body {
  margin-top: 16px;
  flex: 1;
  min-height: 0;
}

.dash-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

@media (min-width: 992px) {
  .dash-main {
    flex: 0 0 77.5%;
    max-width: 77.5%;
  }

  .dash-rail {
    flex: 0 0 22.5%;
    max-width: 22.5%;
  }
}

/* ---- Compact stat cards (smaller than the default .stat-card) ---- */
.dash-stats {
  margin-bottom: 14px;
  row-gap: 10px;
}

.dash-stat--compact {
  min-height: auto;
  padding: 0.6rem 0.85rem;
  gap: 2px;
  border-radius: var(--radius-md);
}

.dash-stat--compact .stat-card__label {
  font-size: 0.6rem;
}

.dash-stat--compact .stat-card__value {
  font-size: 1.35rem;
  margin: 1px 0;
}

.dash-stat--compact .stat-card__delta {
  font-size: 0.68rem;
}

.stat-card__muted {
  color: var(--color-text-muted);
  font-weight: 400;
}

/* ---- Main display panel ---- */
.display-panel {
  background: var(--color-bg-canvas);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.display-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--color-border);
}

.display-toolbar__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.display-toolbar__right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.screen-select {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-bg-canvas);
  color: var(--color-text-primary);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.screen-select:hover {
  border-color: var(--color-primary-border);
}

.screen-select.dropdown-toggle::after,
.screen-select.dropdown-toggle::before {
  display: none !important;
}

.screen-select .pi-chevron-down {
  font-size: 10px;
  color: var(--color-text-muted);
}

.screen-select__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-success);
  flex-shrink: 0;
}

.screen-select__dot.is-off {
  background: var(--color-danger);
}

.spec-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-size: 0.72rem;
  font-weight: 500;
  white-space: nowrap;
}

.toolbar-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}

.toolbar-icon-btn:hover {
  background: var(--color-bg-muted);
  color: var(--color-primary-hover);
  border-color: var(--color-border);
}

.toolbar-icon-btn .pi {
  font-size: 0.85rem;
}

.toolbar-icon-btn.dropdown-toggle::after,
.toolbar-icon-btn.dropdown-toggle::before {
  display: none !important;
}

.display-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: var(--color-bg-soft);
  padding: 0px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.display-stage :deep(.screen-display-root--embedded .screen-background-image) {
  max-width: 100% !important;
  max-height: 100% !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
}

/* On desktop, remove the image from normal flow so a portrait background
   cannot increase the card's height before Display.vue checks for overflow. */
@media (min-width: 992px) {
  .body {
    height: 100vh;
    overflow: hidden;
  }

  .dashboard-display-shell {
    position: absolute;
    inset: 0;
    width: auto;
    height: auto;
  }

  .dashboard-display-shell :deep(.screen-display-root--embedded) {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  .dashboard-display-shell :deep(.screen-display-root--embedded .image-container) {
    position: absolute;
    inset: 0;
    width: auto !important;
    height: auto !important;
    max-width: none !important;
    max-height: none !important;
    overflow: hidden;
  }

  .display-stage :deep(.screen-display-root--embedded .screen-background-image) {
    position: absolute;
    top: var(--embedded-image-offset-y, 0);
    left: var(--embedded-image-offset-x, 0);
    width: var(--embedded-image-width, 100%) !important;
    height: var(--embedded-image-height, 100%) !important;
    max-width: none !important;
    max-height: none !important;
    object-fit: contain !important;
  }
}

@media (min-width: 1200px) {
  html.app-zoomed .body {
    height: calc(100vh / 0.9);
  }
}

.display-empty {
  text-align: center;
  color: var(--color-text-muted);
}

.display-empty .pi {
  font-size: 1.6rem;
  margin-bottom: 6px;
  display: block;
}

/* ---- Right rail (screens list) ---- */
.dash-rail {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-left: 8px;
}

.dash-rail .dash-stats {
  flex: 0 0 auto;
}

.screens-rail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
  overflow: hidden;
}

.screens-rail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.screens-rail__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.screens-rail__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  margin-left: 4px;
  border-radius: var(--radius-pill);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-size: 0.72rem;
  font-weight: 600;
}

.screens-tabs {
  width: 100%;
}

.screens-tabs .segmented__option {
  padding: 6px 4px;
  font-size: 0.74rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.screens-tabs__count {
  color: var(--color-text-muted);
  font-size: 0.7rem;
}

.screens-tabs .segmented__option.is-active .screens-tabs__count {
  color: var(--color-primary-hover);
}

.screens-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-right: 2px;
}

/* Larger, card-like screen rows in the rail */
.screens-list .screen-row {
  gap: 14px;
  padding: 12px 14px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  background: var(--color-bg-canvas);
}

.screens-list .screen-row__thumb {
  width: 84px;
  height: 54px;
  border-radius: var(--radius-md);
}

.screens-list .screen-row__name {
  font-size: 0.9rem;
}

.screen-row__status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  min-width: 0;
}

.screen-row__status .status-pill {
  padding: 2px 10px;
  font-size: 0.64rem;
}

.screen-row__status .screen-row__sub {
  font-size: 0.7rem;
}

.screen-row__status .screen-row__sub {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.screen-row__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.screen-row__icon-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}

.screen-row__icon-btn:hover {
  background: var(--color-bg-soft);
  color: var(--color-primary);
  border-color: var(--color-border);
}

.screen-row__icon-btn .pi {
  font-size: 0.9rem;
}

.screens-empty {
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.8rem;
  padding: 24px 0;
}

/* =========================================================================
   MOBILE & TABLET (≤ 991.98px)
   -------------------------------------------------------------------------
   The desktop layout (≥ 992px) is intentionally left completely untouched.
   The mobile chrome below (top app bar, bottom nav, sync strip) is hidden by
   default and only revealed within these media queries.
   ========================================================================= */
.m-topbar,
.m-syncbar {
  display: none;
}

@media (max-width: 991.98px) {
  /* --- Swap desktop chrome for the mobile chrome --- */
  .custom-col-width-2 { display: none !important; }      /* desktop sidenav */
  .app-topbar { display: none !important; }              /* desktop navbar  */

  .content-body > .row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .custom-col-width-10,
  .collapsed-col {
    width: 100% !important;
  }

  .content-body {
    overflow-x: hidden;
  }

  /* `html.app-zoomed` sets overflow:hidden (no window scroll), and fixed/sticky
     positioning is unreliable under CSS `zoom`. So build a flex-column app
     shell: fixed top bar + scrollable middle + fixed bottom bar, all as flow
     items. The body fills the viewport (counter-scaled by /0.9 for the zoom). */
  .body {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    overflow: hidden;
    padding-right: 0 !important;
  }

  /* --- Top app bar --- */
  .m-topbar {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 14px;
    background: var(--color-bg-canvas);
    border-bottom: 1px solid var(--color-border);
  }

  .m-topbar__logo {
    height: 26px;
    width: auto;
    object-fit: contain;
  }

  .m-topbar__actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .m-icon-btn {
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: background-color .15s ease, color .15s ease;
  }

  .m-icon-btn:hover {
    background: var(--color-bg-muted);
    color: var(--color-primary-hover);
  }

  .m-icon-btn .pi { font-size: 1rem; }

  .m-avatar {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--color-primary);
    color: var(--color-text-on-primary);
    font-size: 0.72rem;
    font-weight: 700;
    margin-left: 2px;
  }

  /* --- Page is the scrollable middle of the shell --- */
  .dashboard-page {
    display: block;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 14px 14px 22px 14px;
  }

  .dash-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .dash-header__actions {
    width: 100%;
    margin-bottom: 6px;
    flex-wrap: wrap;
    row-gap: 8px;
  }

  .dash-header__actions .dash-preview-picker {
    max-width: 100%;
  }

  /* Stat cards: horizontal, snap-scrolling rail (peek the next card) */
  .dash-stats {
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin: 0 0 14px 0;
    -webkit-overflow-scrolling: touch;
  }

  .dash-stats::-webkit-scrollbar { display: none; }

  .dash-stats > [class*='col-'] {
    flex: 0 0 78%;
    max-width: 280px;
    padding-left: 0;
    padding-right: 10px;
    scroll-snap-align: start;
  }

  .dash-body {
    display: block;
    flex: none;
    min-height: 0;
    margin: 16px 0 0 0;
  }

  .dash-main,
  .dash-rail {
    display: block;
    width: 100%;
    max-width: 100%;
    flex: none;
    padding: 0;
  }

  .dash-rail {
    margin-top: 18px;
  }

  /* --- Display panel → natural height --- */
  .display-panel {
    display: block;
    flex: none;
    min-height: 0;
  }

  .display-stage {
    flex: none;
    min-height: 220px;
    padding: 12px;
  }

  .display-stage :deep(.screen-background-image) {
    max-height: 460px !important;
  }

  /* Mobile: the stage has no fixed height here (min-height + a 460px image cap),
     so the root sizes to the contained image rather than a full-height card. */
  .dashboard-display-shell :deep(.screen-display-root--embedded) {
    width: auto !important;
    height: auto !important;
  }

  /* --- Screen list grows with the page (window scrolls, not an inner pane) --- */
  .screens-list {
    flex: none;
    min-height: 0;
    overflow: visible;
    max-height: none;
  }

  /* --- Sync status strip --- */
  .m-syncbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding: 10px 12px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg-canvas);
    font-size: 0.74rem;
    color: var(--color-text-muted);
  }

  .m-syncbar__status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: var(--color-text-secondary);
    font-weight: 500;
  }

  .m-syncbar__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-success);
  }

  .m-syncbar__ver { font-size: 0.72rem; }
}

/* =========================================================================
   MOBILE ONLY (≤ 575.98px)
   ========================================================================= */
@media (max-width: 575.98px) {
  .dash-header__actions {
    display: flex;
  }

  /* The picker takes a row of its own, its panel the width of it. */
  .dash-header__actions .dash-preview-picker {
    flex: 1 1 100%;
  }

  /* Toolbar: drop the spec chips, keep the selector + actions */
  .spec-chip { display: none; }

  .display-toolbar { padding: 8px; }

  .screen-select { max-width: 58vw; }

  .display-stage :deep(.screen-background-image) {
    max-height: 380px !important;
  }
}
</style>
